// src/lib/pwa.js
// Service-worker registration and install-prompt plumbing.
//
// Deliberately does not auto-reload when a new build appears: a POS shouldn't
// refresh itself while someone is halfway through taking an order. The app
// surfaces a banner and the person clicks it when convenient.

let waitingWorker = null;

export function registerServiceWorker({ onUpdateReady } = {}) {
  if (process.env.NODE_ENV !== 'production') return;   // dev + SW = pain
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');

      // Already an update sitting there from a previous visit.
      if (reg.waiting && navigator.serviceWorker.controller) {
        waitingWorker = reg.waiting;
        onUpdateReady?.();
      }

      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            waitingWorker = installing;
            onUpdateReady?.();
          }
        });
      });
    } catch (err) {
      console.warn('[pwa] service worker registration failed', err);
    }
  });
}

/** Activate the waiting worker and reload once it takes over. */
export function applyUpdate() {
  if (!waitingWorker) { window.location.reload(); return; }
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  waitingWorker.postMessage('SKIP_WAITING');
}

// ─── Install prompt ───────────────────────────────────────────────────────────

let deferredPrompt = null;
const installListeners = new Set();
const notify = () => installListeners.forEach(fn => fn(canInstall()));

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Chrome/Edge fire this when the app meets the install criteria. Holding on
    // to it lets us offer our own button instead of relying on staff spotting
    // the small icon in the address bar.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

export function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
}

export function canInstall() {
  return !!deferredPrompt && !isStandalone();
}

export function onInstallAvailabilityChange(fn) {
  installListeners.add(fn);
  return () => installListeners.delete(fn);
}

/** Returns 'accepted' | 'dismissed' | 'unavailable'. */
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  if (outcome === 'accepted') deferredPrompt = null;
  notify();
  return outcome;
}
